// Service for artifact type and deployment channel definitions
// This structure allows easy integration with backend APIs in the future

export interface ArtifactTypeDefinition {
  type: string;
  definition: string;
  aiReasoning: string;
  examples?: string[];
}

export interface DeploymentChannelDefinition {
  channel: string;
  definition: string;
  suggestions: string[];
  bestPractices?: string;
}

// Sample data - can be easily replaced with API calls
const ARTIFACT_TYPE_DEFINITIONS: Record<string, ArtifactTypeDefinition> = {
  "Comparison Asset": {
    type: "Comparison Asset",
    definition: "Structured content that helps AI models understand how your product compares to alternatives in your category.",
    aiReasoning: "LLMs use comparison content to build relational understanding. When users ask 'what's the difference between X and Y', models rely on explicit comparison frameworks to generate accurate, contextual responses.",
    examples: [
      "Feature comparison tables",
      "Use case scenarios",
      "When to choose guides"
    ]
  },
  "Decision Framework": {
    type: "Decision Framework",
    definition: "Structured guides that help users and AI models understand how to evaluate and choose between different options in your product category.",
    aiReasoning: "AI models prioritize content that provides clear decision-making frameworks. When users ask 'how do I choose' or 'which option is best for me', models reference explicit decision criteria to provide contextually appropriate recommendations.",
    examples: [
      "Category definition guides",
      "Use case decision trees",
      "Selection criteria frameworks"
    ]
  },
  "Brand Voice Guidelines": {
    type: "Brand Voice Guidelines",
    definition: "Documentation that defines your brand's tone, messaging principles, and communication style for AI models to reference.",
    aiReasoning: "Voice guidelines help AI models maintain brand consistency when generating summaries or recommendations. Clear style documentation improves the quality and alignment of AI-generated content about your brand.",
    examples: [
      "Tone and voice documentation",
      "Brand messaging pillars",
      "Communication style guide"
    ]
  },
  "Structured Data": {
    type: "Structured Data",
    definition: "Machine-readable data formats (JSON-LD, Schema.org, etc.) that provide AI models with high-confidence, structured information about your products and services.",
    aiReasoning: "Structured data is the highest-confidence source for AI models. Unlike unstructured content, schema markup and structured formats eliminate ambiguity and dramatically reduce hallucination risk in AI-generated responses.",
    examples: [
      "Schema.org markup (Product, Organization, FAQ)",
      "JSON-LD structured data",
      "Pricing and availability feeds"
    ]
  },
  "Schema Markup": {
    type: "Schema Markup",
    definition: "Structured data that makes your content machine-readable, helping AIs extract precise information about your products, services, and organization.",
    aiReasoning: "Schema.org markup provides a standardized vocabulary that AI models can parse with high confidence. This structured approach reduces hallucination and improves factual accuracy in AI-generated responses.",
    examples: [
      "Product schema with pricing/availability",
      "Organization schema with contact info",
      "FAQ schema for common questions"
    ]
  },
  "Voice Guidelines": {
    type: "Voice Guidelines",
    definition: "Documentation that defines your brand's tone, messaging principles, and communication style for AI models to reference.",
    aiReasoning: "Voice guidelines help AI models maintain brand consistency when generating summaries or recommendations. Clear style documentation improves the quality and alignment of AI-generated content about your brand.",
    examples: [
      "Tone and voice documentation",
      "Brand messaging pillars",
      "Communication style guide"
    ]
  },
  "FAQ Content": {
    type: "FAQ Content",
    definition: "Frequently asked questions with detailed answers that address common user queries and objections.",
    aiReasoning: "FAQ content is one of the most referenced content types by AI models. Well-structured Q&A pairs directly feed into conversational AI responses and help models understand user intent patterns.",
    examples: [
      "Product FAQ sections",
      "Support documentation",
      "Common objections & responses"
    ]
  }
};

const DEPLOYMENT_CHANNEL_DEFINITIONS: Record<string, DeploymentChannelDefinition> = {
  "Owned site": {
    channel: "Owned site",
    definition: "Content deployed on domains you control, where you have full editorial and technical control.",
    suggestions: [
      "Create a dedicated /compare or /vs page for comparison content",
      "Build a /choose or /solutions page to help users find the right product",
      "Add structured FAQ sections to product pages",
      "Implement schema markup across all product and service pages"
    ],
    bestPractices: "Ensure all content is indexable and accessible to AI crawlers. Use clean URLs and proper semantic HTML."
  },
  "Third-party blogs": {
    channel: "Third-party blogs",
    definition: "Guest posts, contributed articles, or sponsored content on external publications and industry blogs.",
    suggestions: [
      "Partner with industry publications for thought leadership articles",
      "Contribute comparison guides to relevant tech blogs",
      "Publish use case studies on partner sites",
      "Create guest posts that naturally reference your positioning"
    ],
    bestPractices: "Focus on authoritative sites with high domain credibility. Include factual, non-promotional content that AI models can trust."
  },
  "Review Platforms": {
    channel: "Review Platforms",
    definition: "Content and structured data on third-party review sites like G2, Capterra, TrustRadius, etc.",
    suggestions: [
      "Claim and optimize your company profiles on major review platforms",
      "Encourage customers to leave detailed, feature-specific reviews",
      "Respond to reviews with helpful, informative answers",
      "Keep product information and feature lists up-to-date"
    ],
    bestPractices: "Review platforms are heavily weighted by AI models for product research queries. Keep profiles current and respond professionally."
  },
  "Forums": {
    channel: "Forums",
    definition: "Community discussions on platforms like Reddit, Stack Overflow, Quora, and industry-specific forums.",
    suggestions: [
      "Participate authentically in relevant community discussions",
      "Answer user questions with helpful, non-promotional responses",
      "Share comparison insights when users ask for alternatives",
      "Build thought leadership through consistent, valuable contributions"
    ],
    bestPractices: "Avoid overt promotion. AI models favor authentic, helpful forum content. Focus on being genuinely useful to the community."
  },
  "Structured Data": {
    channel: "Structured Data",
    definition: "Machine-readable markup and APIs that provide direct data access for AI systems.",
    suggestions: [
      "Implement comprehensive schema.org markup across your site",
      "Provide JSON-LD structured data for products and services",
      "Create API documentation that's publicly accessible",
      "Use OpenGraph and Twitter Card meta tags for social sharing"
    ],
    bestPractices: "Structured data is the highest-confidence source for AI models. Prioritize accuracy and keep data current."
  }
};

// Service functions that can be replaced with API calls
export const artifactDefinitionsService = {
  getArtifactTypeDefinition: async (type: string): Promise<ArtifactTypeDefinition | null> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 0));
    return ARTIFACT_TYPE_DEFINITIONS[type] || null;
  },

  getDeploymentChannelDefinition: async (channel: string): Promise<DeploymentChannelDefinition | null> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 0));
    return DEPLOYMENT_CHANNEL_DEFINITIONS[channel] || null;
  },

  // Future: These would be actual API calls
  // getArtifactTypeDefinition: async (type: string) => {
  //   const response = await fetch(`/api/artifact-definitions/${encodeURIComponent(type)}`);
  //   return response.json();
  // },
};